import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BellIcon, XIcon, CheckIcon, AlertCircleIcon, CheckCircleIcon, InfoIcon, SettingsIcon } from 'lucide-react';
import { useNotifications } from '../../hooks/useNotifications';
import { formatDistanceToNow } from '../../utils/dateUtils';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Modal, ModalFooter } from '../ui/Modal';
import { Checkbox } from '../ui/Checkbox';
export function NotificationCenter() {
  const {
    notifications,
    preferences,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    updatePreferences,
    getUnreadCount
  } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const unreadCount = getUnreadCount();
  const getNotificationIcon = (type: string) => {
    if (type.includes('completed')) return <CheckCircleIcon className="w-5 h-5 text-success-500" data-id="element-104" />;
    if (type.includes('failed')) return <AlertCircleIcon className="w-5 h-5 text-error-500" data-id="element-105" />;
    if (type.includes('alert')) return <AlertCircleIcon className="w-5 h-5 text-warning-500" data-id="element-106" />;
    return <InfoIcon className="w-5 h-5 text-primary-500" data-id="element-107" />;
  };
  return <>
      {/* Notification Bell Button */}
      <div className="relative" data-id="element-108">
        <motion.button onClick={() => setIsOpen(!isOpen)} className="relative p-2 text-surface-600 hover:text-surface-900 hover:bg-surface-100 rounded-lg transition-colors" whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.95
      }} data-id="element-109">
          <BellIcon className="w-6 h-6" data-id="element-110" />
          {unreadCount > 0 && <motion.span initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} className="absolute top-1 right-1 w-5 h-5 bg-error-500 text-white text-xs font-bold rounded-full flex items-center justify-center" data-id="element-111">
              {unreadCount > 9 ? '9+' : unreadCount}
            </motion.span>}
        </motion.button>

        {/* Notification Dropdown */}
        <AnimatePresence data-id="element-112">
          {isOpen && <motion.div initial={{
          opacity: 0,
          y: 10,
          scale: 0.95
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 10,
          scale: 0.95
        }} transition={{
          duration: 0.2
        }} className="absolute right-0 mt-2 w-96 max-h-[600px] bg-white rounded-xl shadow-2xl border border-surface-200 overflow-hidden z-50" data-id="element-113">
              {/* Header */}
              <div className="p-4 border-b border-surface-100 flex items-center justify-between bg-surface-50" data-id="element-114">
                <div data-id="element-115">
                  <h3 className="text-heading-md font-bold text-surface-900" data-id="element-116">
                    Notifications
                  </h3>
                  {unreadCount > 0 && <p className="text-caption text-surface-500" data-id="element-117">
                      {unreadCount} unread
                    </p>}
                </div>
                <div className="flex items-center gap-2" data-id="element-118">
                  <Button variant="ghost" size="sm" onClick={() => setShowSettings(true)} icon={<SettingsIcon className="w-4 h-4" data-id="element-120" />} data-id="element-119" />
                  {unreadCount > 0 && <Button variant="ghost" size="sm" onClick={markAllAsRead} icon={<CheckIcon className="w-4 h-4" data-id="element-122" />} data-id="element-121">
                      Mark all read
                    </Button>}
                </div>
              </div>

              {/* Notifications List */}
              <div className="max-h-[500px] overflow-y-auto" data-id="element-123">
                {notifications.length === 0 ? <div className="p-8 text-center" data-id="element-124">
                    <BellIcon className="w-12 h-12 text-surface-300 mx-auto mb-3" data-id="element-125" />
                    <p className="text-body-md text-surface-500" data-id="element-126">
                      No notifications yet
                    </p>
                  </div> : <div className="divide-y divide-surface-100" data-id="element-127">
                    {notifications.map(notification => <motion.div key={notification.id} initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} className={`p-4 hover:bg-surface-50 transition-colors cursor-pointer ${!notification.read ? 'bg-primary-50/30' : ''}`} onClick={() => markAsRead(notification.id)} data-id="element-128">
                        <div className="flex items-start gap-3" data-id="element-129">
                          <div className="flex-shrink-0 mt-0.5" data-id="element-130">
                            {getNotificationIcon(notification.type)}
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-131">
                            <div className="flex items-start justify-between gap-2" data-id="element-132">
                              <h4 className="text-body-md font-semibold text-surface-900" data-id="element-133">
                                {notification.title}
                              </h4>
                              {!notification.read && <div className="w-2 h-2 bg-primary-500 rounded-full flex-shrink-0 mt-1.5" data-id="element-134" />}
                            </div>
                            <p className="text-body-sm text-surface-600 mt-1" data-id="element-135">
                              {notification.message}
                            </p>
                            <p className="text-caption text-surface-400 mt-2" data-id="element-136">
                              {formatDistanceToNow(notification.createdAt)}
                            </p>
                          </div>
                          <button onClick={e => {
                    e.stopPropagation();
                    deleteNotification(notification.id);
                  }} className="flex-shrink-0 p-1 text-surface-400 hover:text-surface-600 hover:bg-surface-100 rounded transition-colors" data-id="element-137">
                            <XIcon className="w-4 h-4" data-id="element-138" />
                          </button>
                        </div>
                      </motion.div>)}
                  </div>}
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Settings Modal */}
      <Modal isOpen={showSettings} onClose={() => setShowSettings(false)} title="Notification Preferences" size="md" data-id="element-139">
        <div className="space-y-6" data-id="element-140">
          <div data-id="element-141">
            <h4 className="text-body-md font-semibold text-surface-900 mb-3" data-id="element-142">
              Channels
            </h4>
            <div className="space-y-3" data-id="element-143">
              <Checkbox label="Email notifications" checked={preferences.email} onChange={checked => updatePreferences({
              email: checked
            })} data-id="element-144" />
              <Checkbox label="SMS notifications" checked={preferences.sms} onChange={checked => updatePreferences({
              sms: checked
            })} data-id="element-145" />
              <Checkbox label="Push notifications" checked={preferences.push} onChange={checked => updatePreferences({
              push: checked
            })} data-id="element-146" />
            </div>
          </div>

          <div className="border-t border-surface-200 pt-6" data-id="element-147">
            <h4 className="text-body-md font-semibold text-surface-900 mb-3" data-id="element-148">
              Notification Types
            </h4>
            <div className="space-y-3" data-id="element-149">
              <Checkbox label="Refund updates" checked={preferences.refundUpdates} onChange={checked => updatePreferences({
              refundUpdates: checked
            })} data-id="element-150" />
              <Checkbox label="Withdrawal updates" checked={preferences.withdrawalUpdates} onChange={checked => updatePreferences({
              withdrawalUpdates: checked
            })} data-id="element-151" />
              <Checkbox label="Promotional offers" checked={preferences.promoOffers} onChange={checked => updatePreferences({
              promoOffers: checked
            })} data-id="element-152" />
            </div>
          </div>
        </div>

        <ModalFooter data-id="element-153">
          <Button variant="secondary" onClick={() => setShowSettings(false)} data-id="element-154">
            Cancel
          </Button>
          <Button onClick={() => setShowSettings(false)} data-id="element-155">
            Save Preferences
          </Button>
        </ModalFooter>
      </Modal>
    </>;
}